import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Label } from '@iptv/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { adminFetch, type ApiError } from '../lib/api';
import { useTotpChallenge } from '../lib/totp';

interface SiteWithConfig {
  id: number;
  slug: string;
  domain: string;
  theme: string;
  active_config: { id: number; version: number } | null;
}

interface ConfigVersion {
  id: number;
  version: number;
  is_active: boolean;
  published_by: number | null;
  published_at: string | null;
  created_at: string;
}

interface ConfigPayload {
  content: Record<string, unknown>;
}

export function SiteEditorPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const challengeTotp = useTotpChallenge();

  const [draft, setDraft] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [savedVersion, setSavedVersion] = useState<number | null>(null);

  const site = useQuery({
    queryKey: ['admin', 'sites', id],
    queryFn: () => adminFetch<SiteWithConfig>(`/api/v1/admin/sites/${id}`),
    enabled: Boolean(id),
  });

  const history = useQuery({
    queryKey: ['admin', 'sites', id, 'history'],
    queryFn: () =>
      adminFetch<ConfigVersion[]>(`/api/v1/admin/sites/${id}/configs/history`),
    enabled: Boolean(id),
  });

  const config = useQuery({
    queryKey: ['admin', 'sites', id, 'config'],
    queryFn: () =>
      adminFetch<{ content: Record<string, unknown> }>(
        `/api/v1/sites/${site.data?.slug}/config`,
      ),
    enabled: Boolean(site.data?.slug),
  });

  useEffect(() => {
    if (config.data?.content) {
      setDraft(JSON.stringify(config.data.content, null, 2));
    }
  }, [config.data]);

  const saveDraft = useMutation({
    mutationFn: () => {
      try {
        const parsed = JSON.parse(draft) as Record<string, unknown>;
        setParseError(null);
        return adminFetch<{ id: number; version: number }>(
          `/api/v1/admin/sites/${id}/configs`,
          { method: 'POST', body: { content: parsed } as ConfigPayload },
        );
      } catch (e) {
        setParseError(`Invalid JSON: ${(e as Error).message}`);
        throw e;
      }
    },
    onSuccess: (data) => {
      setSavedVersion(data.version);
      queryClient.invalidateQueries({ queryKey: ['admin', 'sites', id] });
    },
  });

  const publish = useMutation({
    mutationFn: ({ version, token }: { version: number; token: string }) =>
      adminFetch<ConfigVersion>(
        `/api/v1/admin/sites/${id}/configs/${version}/publish`,
        { method: 'POST', body: { totp_challenge_token: token } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'sites', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'sites'] });
      setSavedVersion(null);
    },
  });

  async function handleSaveAndPublish() {
    setParseError(null);
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(draft) as Record<string, unknown>;
    } catch (e) {
      setParseError(`Invalid JSON: ${(e as Error).message}`);
      return;
    }
    const token = await challengeTotp({
      message: `Publish a new ${site.data?.slug} config (irreversible without a rollback).`,
    });
    if (!token) return;
    try {
      const draftRes = await adminFetch<{ version: number }>(
        `/api/v1/admin/sites/${id}/configs`,
        { method: 'POST', body: { content: parsed } as ConfigPayload },
      );
      await publish.mutateAsync({ version: draftRes.version, token });
    } catch (err) {
      alert((err as ApiError).detail ?? 'Publish failed');
    }
  }

  async function handlePublishVersion(version: number) {
    if (!confirm(`Publish version ${version}? It will replace the current active config.`))
      return;
    const token = await challengeTotp({
      message: `Switch active config to version ${version}.`,
    });
    if (!token) return;
    try {
      await publish.mutateAsync({ version, token });
    } catch (err) {
      alert((err as ApiError).detail ?? 'Publish failed');
    }
  }

  if (site.isPending) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4">
      <Link to="/sites" className="inline-flex items-center gap-1 text-sm text-muted-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to sites
      </Link>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {site.data?.slug} — {site.data?.domain}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Theme: {site.data?.theme} · Active version:{' '}
            {site.data?.active_config?.version ?? '—'}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <Label htmlFor="cfg">Config (JSON)</Label>
          <textarea
            id="cfg"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-[480px] w-full rounded-md border bg-background p-3 font-mono text-xs"
            spellCheck={false}
          />
          {parseError && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {parseError}
            </div>
          )}
          {savedVersion !== null && (
            <div className="rounded-md bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">
              Draft saved as v{savedVersion}. Click &ldquo;Save &amp; publish&rdquo; to make it live.
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => saveDraft.mutate()}
              disabled={saveDraft.isPending}
            >
              {saveDraft.isPending ? 'Saving…' : 'Save as draft'}
            </Button>
            <Button onClick={handleSaveAndPublish} disabled={publish.isPending}>
              {publish.isPending ? 'Publishing…' : 'Save & publish'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Version history</CardTitle>
        </CardHeader>
        <CardContent>
          {history.isPending && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {history.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">No versions yet.</p>
          )}
          <ul className="space-y-2">
            {history.data?.map((v) => (
              <li
                key={v.id}
                className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono">v{v.version}</span>
                  {v.is_active ? (
                    <Badge variant="success">active</Badge>
                  ) : (
                    <Badge variant="secondary">draft</Badge>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {v.published_at
                      ? `published ${new Date(v.published_at).toLocaleString()}`
                      : `created ${new Date(v.created_at).toLocaleString()}`}
                  </span>
                </div>
                {!v.is_active && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePublishVersion(v.version)}
                    disabled={publish.isPending}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Publish v{v.version}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
